import Box, { type BoxProps } from '@mui/material/Box';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { TableRow, type TableRowPropsWithData } from '@openx/components-table/parts/TableRow';

import { Table, TableName } from '@openx/components-table/Table';
import { expandContentCellClass } from '@openx/types/tableHelpers';
import { type SyntheticEvent, useCallback, useState } from 'react';

import type { ConfirmationTableRow, LalConfirmationTableRowWithAudiences } from '../../../../types';
import { associatedAudiencesTableColumns } from '../tableColumns';

const AssociatedAudiencesTableBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={[
      {
        bgcolor: 'background.boxLight',
      },
      theme => ({
        '& .MuiTableCell-body': {
          padding: theme.spacing(1, 0),
        },

        '& .MuiTableCell-head': {
          color: theme.palette.text.secondary,
          fontSize: '12px',
          lineHeight: '22px',
          padding: theme.spacing(0.5, 0),
        },
      }),
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

const ExpandComponent = ({ data }) => {
  const { segment_audiences: segmentAudiences } = data;
  const associatedAudiences = segmentAudiences.map(segmentAudience => segmentAudience.audience);

  return (
    <AssociatedAudiencesTableBox data-test="associated-audiences-dialog-table" sx={{ bgcolor: 'background.boxLight' }}>
      {associatedAudiences.length ? (
        <Table
          name={TableName.ASSOCIATED_AUDIENCES}
          columns={associatedAudiencesTableColumns}
          data={associatedAudiences}
          enableTableHeaderCustomization={false}
          overrideTableCellStyles={{
            '&.MuiTableCell-body:last-of-type': {
              paddingRight: 1,
            },
            '&:first-of-type': {
              paddingLeft: 10.5,
            },
            height: 'auto',
          }}
          loading={false}
        />
      ) : (
        <Box
          data-test="associated-audiences-dialog-table-placeholder"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 36,
          }}
        >
          <Typography
            variant="h3"
            component="span"
            color="textSecondary"
            style={{
              fontSize: 12,
              textTransform: 'uppercase',
            }}
          >
            No Audiences Found
          </Typography>
        </Box>
      )}
    </AssociatedAudiencesTableBox>
  );
};

export const ExpandableTableRow = (
  rowProps: TableRowPropsWithData<ConfirmationTableRow | LalConfirmationTableRowWithAudiences>,
  isForcedExpanded?: (data: ConfirmationTableRow | LalConfirmationTableRowWithAudiences) => boolean
) => {
  const { rowData, onRowClick, columns } = rowProps;

  const [expanded, setExpanded] = useState<boolean>(isForcedExpanded ? isForcedExpanded(rowData) : false);

  const onClick = useCallback(
    (data: ConfirmationTableRow | LalConfirmationTableRowWithAudiences, e: SyntheticEvent) => {
      const attr = (e.target as HTMLElement).dataset.expandicon;
      const { parentNode } = e.target as HTMLElement;
      const parentAttr = parentNode && (parentNode as HTMLElement).dataset.expandicon;

      if ([attr, parentAttr].indexOf('expandRow') !== -1) {
        setExpanded(expanded => !expanded);
      } else {
        onRowClick?.(data, e);
      }
    },
    [onRowClick]
  );

  const additionalRenderParams = {
    expanded,
  };

  return (
    <>
      <TableRow
        key={rowData.id}
        columns={columns}
        data-test="table-row-expand"
        onRowClick={onClick}
        rowData={{ ...rowData, ...additionalRenderParams }}
        overrideTableCellStyles={rowProps.overrideTableCellStyles}
      />
      {expanded && (
        <MuiTableRow
          key={`segment-expand-audiences-${rowData.id}`}
          selected={expanded}
          sx={{
            '& .MuiTableCell-root.MuiTableCell-paddingNone': {
              padding: 0,
            },
          }}
        >
          <MuiTableCell className={expandContentCellClass} padding="none" colSpan={3}>
            <ExpandComponent data={rowData} />
          </MuiTableCell>
        </MuiTableRow>
      )}
    </>
  );
};
